import { activity } from '@dha-nexus/shared';

const STORAGE_KEY = 'dha_prime__activity_capture_session';
const ACTIVITY_BATCH_PATH = '/api/activity-events/batch';
const DEFAULT_CONFIG = Object.freeze({
  batchMaxEvents: 100,
  batchMaxBytes: 1024 * 1024,
  flushIntervalMs: 5000,
});

let state = {
  config: { ...DEFAULT_CONFIG },
  session: null,
  queue: [],
  queuedBytes: 0,
  sequenceNo: 0,
  flushTimer: null,
  flushPromise: null,
  // Bumped on every identity transition. A failed flush may only re-queue its
  // events when the generation is unchanged — a batch captured under one
  // actor must never be replayed under the next.
  generation: 0,
};

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10).toUpperCase()}`;
}

function currentRouteReference() {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search || ''}`;
}

function currentRoutePath() {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname || '/';
}

function readStoredSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function persistSession() {
  if (typeof window === 'undefined' || !state.session) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.session));
  } catch {
    // Ignore storage failures and continue with in-memory capture.
  }
}

function normalizeConfig(config = {}) {
  return {
    batchMaxEvents: Math.max(Number(config.batchMaxEvents) || DEFAULT_CONFIG.batchMaxEvents, 1),
    batchMaxBytes: Math.max(Number(config.batchMaxBytes) || DEFAULT_CONFIG.batchMaxBytes, 1024),
    flushIntervalMs: Math.max(Number(config.flushIntervalMs) || DEFAULT_CONFIG.flushIntervalMs, 1),
  };
}

function estimateEventBytes(event) {
  try {
    return JSON.stringify(event).length;
  } catch {
    return 0;
  }
}

function clearFlushTimer() {
  if (state.flushTimer) {
    clearTimeout(state.flushTimer);
    state.flushTimer = null;
  }
}

function scheduleFlush() {
  clearFlushTimer();
  if (!state.queue.length) return;
  state.flushTimer = setTimeout(() => {
    void flushActivityQueue();
  }, state.config.flushIntervalMs);
}

function ensureSession({
  initialRoute = currentRouteReference(),
  lastRoute = initialRoute,
  payload = {},
  actorMarker = null,
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null,
} = {}) {
  if (!state.session) {
    const stored = readStoredSession();
    // A stored session is only resumable when it never ended and was recorded
    // for the same actor. An ended or other-actor session must rotate — one
    // capture session may never mix identities.
    const actorMatches =
      !actorMarker || !stored?.actorMarker || stored.actorMarker === actorMarker;
    if (stored?.id && stored?.startedAt && !stored.endedAt && actorMatches) {
      state.session = stored;
      state.sequenceNo = Number(stored.sequenceNo) || 0;
    }
  }

  if (!state.session) {
    state.session = {
      id: makeId('ACT-SESS'),
      startedAt: new Date().toISOString(),
      initialRoute,
      lastRoute,
      userAgent,
      actorMarker: actorMarker || null,
      payload: payload || {},
      sequenceNo: 0,
      startRecorded: false,
      endedAt: null,
    };
  } else {
    state.session = {
      ...state.session,
      initialRoute: state.session.initialRoute || initialRoute,
      lastRoute: lastRoute || state.session.lastRoute,
      userAgent: state.session.userAgent || userAgent,
      actorMarker: state.session.actorMarker || actorMarker || null,
      payload: {
        ...(state.session.payload || {}),
        ...(payload || {}),
      },
    };
  }

  persistSession();
  return state.session;
}

function buildSessionEnvelope() {
  if (!state.session) return null;
  return {
    id: state.session.id,
    startedAt: state.session.startedAt,
    initialRoute: state.session.initialRoute,
    lastRoute: state.session.lastRoute,
    userAgent: state.session.userAgent,
    payload: state.session.payload || {},
  };
}

function shouldFlushNow() {
  return (
    state.queue.length >= state.config.batchMaxEvents ||
    state.queuedBytes >= state.config.batchMaxBytes
  );
}

function sendWithFetch(body) {
  return fetch(ACTIVITY_BATCH_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    keepalive: true,
    body,
  });
}

function sendWithBeacon(body) {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
  try {
    const blob = new Blob([body], { type: 'application/json' });
    return navigator.sendBeacon(ACTIVITY_BATCH_PATH, blob);
  } catch {
    return false;
  }
}

export function initializeActivityCapture({
  config,
  initialRoute,
  lastRoute,
  payload,
  routePath,
  routeTitle,
  actorMarker,
} = {}) {
  if (config) state.config = normalizeConfig(config);
  ensureSession({ initialRoute, lastRoute, payload, actorMarker });
  if (!state.session.startRecorded) {
    recordActivityEvent({
      eventType: 'session',
      eventName: 'session.start',
      source: 'shell',
      routePath: routePath || currentRoutePath(),
      routeTitle: routeTitle || null,
      payload: {
        initialRoute: state.session.initialRoute,
      },
    });
    state.session.startRecorded = true;
    persistSession();
  } else {
    scheduleFlush();
  }
  return buildSessionEnvelope();
}

export function updateActivitySession(patch = {}) {
  ensureSession();
  state.session = {
    ...state.session,
    ...patch,
    payload: {
      ...(state.session.payload || {}),
      ...(patch.payload || {}),
    },
  };
  persistSession();
  return buildSessionEnvelope();
}

export function recordActivityEvent(event = {}) {
  if (!event?.eventName) return null;
  const expectedType =
    event.eventType || activity.resolveExpectedActivityEventType(event.eventName) || null;
  if (!expectedType || !activity.isValidActivityEventName(event.eventName)) return null;

  ensureSession();
  state.sequenceNo += 1;
  state.session.sequenceNo = state.sequenceNo;
  state.session.lastRoute = event.lastRoute || state.session.lastRoute || currentRouteReference();
  persistSession();

  const normalizedEvent = {
    clientEventId: event.clientEventId || makeId('ACT-EVT'),
    sequenceNo: state.sequenceNo,
    eventType: expectedType,
    eventName: event.eventName,
    source: event.source || 'web',
    routePath: event.routePath || currentRoutePath(),
    routeTitle: event.routeTitle || null,
    targetType: event.targetType ?? null,
    targetId: event.targetId ?? null,
    targetLabel: event.targetLabel ?? null,
    fieldName: event.fieldName ?? null,
    valueText: event.valueText ?? null,
    valueJson: event.valueJson ?? null,
    requestId: event.requestId ?? null,
    occurredAt: event.occurredAt || new Date().toISOString(),
    payload: event.payload || {},
  };

  state.queue.push(normalizedEvent);
  state.queuedBytes += estimateEventBytes(normalizedEvent);

  if (shouldFlushNow()) {
    void flushActivityQueue();
  } else {
    scheduleFlush();
  }

  return normalizedEvent;
}

export async function flushActivityQueue({ preferBeacon = false } = {}) {
  if (state.flushPromise) return state.flushPromise;
  if (!state.queue.length || !state.session) return null;

  clearFlushTimer();
  const generation = state.generation;
  const requeue = (events, size) => {
    // A batch may only return to the queue within its own identity
    // generation; after a transition it belonged to the previous actor and
    // is dropped rather than replayed under the next one.
    if (state.generation !== generation) return;
    state.queue.unshift(...events);
    state.queuedBytes += size;
  };
  const payload = {
    session: buildSessionEnvelope(),
    events: state.queue.splice(0, state.queue.length),
  };
  const payloadSize = state.queuedBytes;
  state.queuedBytes = 0;
  const body = JSON.stringify(payload);

  if (preferBeacon && sendWithBeacon(body)) {
    return { acceptedCount: payload.events.length, lastSequenceNo: state.sequenceNo };
  }

  if (typeof fetch !== 'function') {
    requeue(payload.events, payloadSize);
    return null;
  }

  const request = sendWithFetch(body);
  if (!request || typeof request.then !== 'function') {
    requeue(payload.events, payloadSize);
    return null;
  }

  state.flushPromise = request
    .then(async (response) => {
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }
      const data = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : null;
      return data?.data || {
        acceptedCount: payload.events.length,
        lastSequenceNo: state.sequenceNo,
      };
    })
    .catch((error) => {
      requeue(payload.events, payloadSize);
      if (state.generation === generation) scheduleFlush();
      throw error;
    })
    .finally(() => {
      state.flushPromise = null;
    });

  return state.flushPromise;
}

// Identity transition barrier. Call BEFORE any auth mutation (login, logout,
// persona switch): the pending batch is detached and delivered bound to the
// OLD session while the old auth cookie is still in place, and the capture
// session rotates so the next recorded event starts a fresh session for the
// next identity. Delivery is best-effort (beacon, then fire-and-forget
// fetch): if it fails, that telemetry is deliberately dropped — attribution
// integrity beats completeness, and a batch must never be replayed under a
// different actor.
export function prepareActivityIdentityTransition() {
  clearFlushTimer();
  state.generation += 1;
  const events = state.queue.splice(0, state.queue.length);
  state.queuedBytes = 0;
  if (state.session && events.length) {
    const body = JSON.stringify({ session: buildSessionEnvelope(), events });
    if (!sendWithBeacon(body)) {
      try {
        if (typeof fetch === 'function') void sendWithFetch(body).catch(() => {});
      } catch {
        // Dropped by policy — never replayed under the next identity.
      }
    }
  }
  state.session = null;
  state.sequenceNo = 0;
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  }
}

export function closeActivitySession({ routePath, routeTitle } = {}) {
  if (!state.session || state.session.endedAt) return;
  state.session.endedAt = new Date().toISOString();
  persistSession();
  recordActivityEvent({
    eventType: 'session',
    eventName: 'session.end',
    source: 'shell',
    routePath: routePath || currentRoutePath(),
    routeTitle: routeTitle || null,
    payload: {
      endedAt: state.session.endedAt,
    },
  });
  void flushActivityQueue({ preferBeacon: true });
}

export function __resetActivityCaptureForTests() {
  clearFlushTimer();
  state = {
    config: { ...DEFAULT_CONFIG },
    session: null,
    queue: [],
    queuedBytes: 0,
    sequenceNo: 0,
    flushTimer: null,
    flushPromise: null,
    generation: 0,
  };
}
